/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE. 
*/

import { CommunitySectionFragment } from "@cosmos/hygraph-schema";
import { useMessages } from "@epic-mw/i18n";
import { ReactNode } from "react";

import {
	BackgroundContainer,
	CollapsibleCardViewport,
	CommunityCard,
	CommunityForumRow,
	CommunityForumRowProps,
	CosmosButton,
	CosmosRichText,
	CosmosText,
	CosmosTextVariantEnum,
	EdsIcon,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
} from "@components/elements";
import { toShortNum } from "@components/utils/numberFormatters";
import { css } from "@panda/css";
import { HStack, styled, VStack, Stack } from "@panda/jsx";
import { flex, grid } from "@panda/patterns";

export type CommunitySectionProps = CommunitySectionFragment & {
	discussionCategory?: {
		total_topics_weekly_count: number;
		categories: CommunityForumRowProps[];
	};
};

export const CommunitySection = ({
	config,
	discussionCategory,
	forumTitle,
	forumButton,
	resources,
}: CommunitySectionProps): ReactNode => {
	const messages = useMessages();
	const forumFullTitle =
		forumTitle?.indexOf("{postsPerWeek}") !== -1
			? forumTitle?.replace(
					"{postsPerWeek}",
					`(${toShortNum(discussionCategory?.total_topics_weekly_count || 0)} ${messages["epic.cosmos.postsPerWeek"]})`,
				)
			: forumTitle;

	const resourcesCardsMax = Math.max(resources?.cards?.length || 5, 3);

	return (
		<BackgroundContainer
			bgAnimation={config?.backgroundAnimation}
			bgImageAsset={config?.backgroundImage}
			bgFullHeight={config?.backgroundFullHeight}
			edsBackgroundColor={config?.edsBackgroundColor}
		>
			<ResponsiveContainer>
				<SectionContainer>
					<VStack gap="0" alignItems="flex-start" className="community-section">
						{config?.heading?.desc ? (
							<HStack alignItems="center">
								<div className={flex({ alignItems: "stretch", justifyContent: "stretch", aspectRatio: "1 / 1" })}>
									<EdsIcon icon="EpicGames" size="lg" />
								</div>
								{config?.heading?.desc ? (
									<CosmosRichText variant={CosmosTextVariantEnum.headingSM} html={config?.heading?.desc} />
								) : null}
							</HStack>
						) : null}
						{config?.heading?.title?.html ? <styled.div height="eds.dimension.24" /> : null}
						<SectionHeading {...config?.heading} desc={null} mobileTextAlignStart />
						<styled.div height={{ base: "eds.dimension.32" }} />
						<CollapsibleCardViewport
							viewportCss={grid.raw({
								columnGap: "eds.dimension.16",
								rowGap: "eds.dimension.16",
								columns: { base: resourcesCardsMax, xlDown: 3, mdDown: 2, smDown: 1 },
								w: "full",
							})}
						>
							{resources?.cards?.map((data) => (
								<CommunityCard key={data.title} {...data} />
							))}
						</CollapsibleCardViewport>
						<styled.div height={{ base: "eds.dimension.32" }} />
						<HStack gap="eds.dimension.6" width="100%" alignItems="flex-end">
							<Stack
								gap="eds.dimension.6"
								width="100%"
								alignItems={{ base: "flex-end", smDown: "flex-start" }}
								direction={{ base: "row", smDown: "column" }}
							>
								<CosmosText as="h3" className={css({ marginRight: "auto" })} variant={CosmosTextVariantEnum.headingLG}>
									{forumFullTitle}
								</CosmosText>
							</Stack>
							<CosmosButton {...forumButton} />
						</HStack>
						<styled.div height="eds.dimension.24" />
						<VStack
							gap="eds.dimension.2"
							className={css({
								w: "full",
							})}
						>
							{discussionCategory?.categories?.map((data: CommunityForumRowProps, i: number) => (
								<CommunityForumRow
									key={data.name}
									{...data}
									className={css({
										borderTop: i ? "none" : undefined,
										borderBottom: i !== discussionCategory?.categories?.length - 1 ? "none" : undefined,
										roundedTop: i ? 0 : "eds.borderRadius.16",
										roundedBottom: i === discussionCategory?.categories?.length - 1 ? "eds.borderRadius.16" : 0,
									})}
								/>
							))}
						</VStack>
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
